import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCategoryIcon } from '../components/CategoryIcons';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { getArticles, getArticlesByCategoryName } from '../services/api';
// import '../styles/Archive.css';

function Archive() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFilter = queryParams.get('category');
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        if (categoryFilter) {
          // Convert URL param format (hyphenated) to category format
          const formattedCategory = categoryFilter.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          console.log(formattedCategory)
          response = await getArticlesByCategoryName(formattedCategory);
        } else {
          response = await getArticles(1, 100); // Get a large batch of articles for archive
        }
        
        if (!response || !response.success || !response.data) {
          throw new Error('Failed to fetch articles');
        }
        
        setArticles(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [categoryFilter]);
  
  // Function to organize articles by month and year
  const organizeArticlesByPeriod = (articles) => {
    const periods = {};
    
    articles.forEach(article => {
      const date = new Date(article.date);
      const periodKey = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
      
      if (!periods[periodKey]) {
        periods[periodKey] = [];
      }
      
      periods[periodKey].push({
        ...article,
        formattedDate: date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      });
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(periods)
      .map(([period, articles]) => ({
        period,
        articles: articles.sort((a, b) => new Date(b.date) - new Date(a.date))
      }))
      .sort((a, b) => {
        const [aMonth, aYear] = a.period.split(' ');
        const [bMonth, bYear] = b.period.split(' ');
        
        // Compare years first
        if (aYear !== bYear) {
          return parseInt(bYear) - parseInt(aYear);
        }
        
        // If years are the same, compare months
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months.indexOf(bMonth) - months.indexOf(aMonth);
      });
  };
  
  const archiveData = organizeArticlesByPeriod(articles);

  return (
    <div>
      <Header />
      
      <main className="container">
        <section className="section">
          <h1>Archive</h1>
          <p>Browse all our previous articles and tech insights by date.</p>
          
          {categoryFilter && (
            <div className="filter-indicator">
              <p>
                Showing articles in: <strong>{categoryFilter.replace(/-/g, ' ')}</strong>
                <Link to="/archive" className="clear-filter">Clear filter</Link>
              </p>
            </div>
          )}
        </section>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading articles...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <h2>No articles found for this category.</h2>
            <p>How about checking out the <Link to="/archive">all articles</Link>?</p>
          </div>
        ) : archiveData.length === 0 ? (
          <div className="no-results">
            <p>No articles found for this category.</p>
            <Link to="/archive" className="read-more-button">View All Articles</Link>
          </div>
        ) : (
          archiveData.map((period, index) => (
            <section key={index} className="section">
              <div className="archive-period">
                <h2>{period.period}</h2>
              </div>
              
              <div className="archive-list">
                {period.articles.map((article) => (
                  <div key={article._id} className="archive-item">
                    <div className="archive-date">
                      {new Date(article.date).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </div>
                    <div className="archive-content">
                      <div className="article-category">
                        {getCategoryIcon(article.category.name)}
                        {article.category.name}
                      </div>
                      <h3 className="article-title">
                        <Link to={`/article/${article.slug}`}>{article.title}</Link>
                      </h3>
                    </div>
                    <Link to={`/article/${article.slug}`} className="read-more-link">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                      </svg>
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </main>
      
      <Footer />
    </div>
  );
}

export default Archive; 